import React, { createContext, forwardRef, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "./cn";

const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);
const DragContext = createContext(null);

const FloatingPanel = forwardRef(function FloatingPanel(
  {
    as: Component = "div",
    open,
    autoFocus = false,
    onDismiss,
    anchorRef,
    anchorRect,
    draggable = false,
    preserveScrollbars = false,
    portalTarget,
    align = "end",
    side,
    width = 320,
    minWidth = 0,
    maxHeight,
    gap = 6,
    margin = 12,
    zIndex = 100,
    variant = "surface",
    className,
    style,
    children,
    ...props
  },
  forwardedRef
) {
  const localRef = useRef(null);
  const dragRef = useRef(null);
  const [dragPosition, setDragPosition] = useState(null);
  const [position, setPosition] = useState(null);
  const isPositioned = position !== null;

  useEffect(() => {
    if (!open || !onDismiss) return undefined;
    const handlePointer = event => {
      if (event.button !== 0 || localRef.current?.contains(event.target) || anchorRef?.current?.contains?.(event.target)) return;
      if (preserveScrollbars && event.target instanceof HTMLElement) {
        const rect = event.target.getBoundingClientRect();
        if ((event.target.offsetWidth > event.target.clientWidth && event.clientX >= rect.left + event.target.clientWidth) ||
            (event.target.offsetHeight > event.target.clientHeight && event.clientY >= rect.top + event.target.clientHeight)) return;
      }
      onDismiss('outside', event);
    };
    const handleKey = event => {
      if (event.key !== 'Escape') return;
      // A nested picker consumes Escape before its containing editor.
      if (localRef.current?.querySelector('[data-bun-floating-panel]')) return;
      event.preventDefault();
      event.stopPropagation();
      onDismiss('escape', event);
      anchorRef?.current?.focus?.();
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey, true);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey, true);
    };
  }, [anchorRef, onDismiss, open, preserveScrollbars]);

  useEffect(() => {
    if (!open || !draggable) return undefined;
    const move = event => {
      if (!dragRef.current || !localRef.current) return;
      const rect = localRef.current.getBoundingClientRect();
      setDragPosition({
        left: clamp(event.clientX - dragRef.current.x, margin, Math.max(margin, window.innerWidth - rect.width - margin)),
        top: clamp(event.clientY - dragRef.current.y, margin, Math.max(margin, window.innerHeight - rect.height - margin)),
      });
    };
    const stop = () => { dragRef.current = null; };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', stop);
    return () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', stop);
    };
  }, [draggable, margin, open]);

  const startDrag = draggable ? event => {
    if (event.button !== 0 || event.target.closest('button, input, textarea, select, a')) return;
    const rect = localRef.current?.getBoundingClientRect();
    if (rect) dragRef.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
  } : null;

  useLayoutEffect(() => {
    if (open && autoFocus && isPositioned) localRef.current?.focus({ preventScroll: true });
  }, [open, autoFocus, isPositioned]);

  const setPanelRef = useCallback(
    (node) => {
      localRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef]
  );

  const updatePosition = useCallback(() => {
    const anchor = anchorRef?.current;
    const panel = localRef.current;
    if ((!anchor && !anchorRect) || !panel || typeof window === "undefined") return;

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const resolvedAnchorRect = anchorRect || anchor.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const numericWidth = typeof width === "number" ? width : panelRect.width;
    const panelWidth = Math.min(Math.max(minWidth, width === 'anchor' ? resolvedAnchorRect.width : panelRect.width || numericWidth || 0), viewportWidth - margin * 2);
    const heightLimit = Math.min(
      maxHeight || viewportHeight - margin * 2,
      viewportHeight - margin * 2
    );
    const preferredHeight = Math.min(
      Math.max(panelRect.height, panel.scrollHeight + 2),
      heightLimit
    );
    const availableAbove = Math.max(0, resolvedAnchorRect.top - margin - gap);
    const availableBelow = Math.max(0, viewportHeight - resolvedAnchorRect.bottom - margin - gap);
    const placement =
      availableBelow >= preferredHeight || availableBelow >= availableAbove ? "below" : "above";
    const availableHeight = placement === "below" ? availableBelow : availableAbove;
    const renderedMaxHeight = Math.min(heightLimit, availableHeight);
    const renderedHeight = Math.min(preferredHeight, renderedMaxHeight);
    const requestedLeft =
      align === "start"
        ? resolvedAnchorRect.left
        : align === "center"
          ? resolvedAnchorRect.left + resolvedAnchorRect.width / 2 - panelWidth / 2
          : resolvedAnchorRect.right - panelWidth;
    const maxLeft = Math.max(margin, viewportWidth - margin - panelWidth);

    if (side === 'right' && viewportWidth - resolvedAnchorRect.right - gap - margin >= panelWidth) {
      setPosition({
        left: Math.round(resolvedAnchorRect.right + gap),
        top: Math.round(clamp(resolvedAnchorRect.top, margin, Math.max(margin, viewportHeight - margin - preferredHeight))),
        maxHeight: Math.floor(heightLimit),
        placement: 'right',
      });
      return;
    }

    setPosition({
      ...(width === 'anchor' ? { width: panelWidth } : {}),
      left: Math.round(clamp(requestedLeft, margin, maxLeft)),
      maxHeight: Math.floor(renderedMaxHeight),
      placement,
      top: Math.round(
        placement === "below"
          ? Math.max(margin, resolvedAnchorRect.bottom + gap)
          : Math.max(margin, resolvedAnchorRect.top - gap - renderedHeight)
      ),
    });
  }, [align, anchorRef, anchorRect, gap, margin, maxHeight, minWidth, side, width]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      setDragPosition(null);
      return undefined;
    }
    updatePosition();
    return undefined;
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open || typeof window === "undefined") return undefined;
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updatePosition);
    if (localRef.current) observer?.observe(localRef.current);
    // Coordinate anchors provide geometry but are not observable DOM elements.
    if (anchorRef?.current instanceof Element) observer?.observe(anchorRef.current);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      observer?.disconnect();
    };
  }, [anchorRef, open, updatePosition]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <Component
      ref={setPanelRef}
      data-bun-floating-panel
      data-placement={position?.placement}
      className={cn(
        "fixed max-w-[calc(100vw-1.5rem)] overflow-y-auto font-instrument",
        variant === "surface" &&
          "rounded-bun-lg border border-bun-line bg-bun-surface text-bun-text shadow-bun-menu",
        variant === "tooltip" &&
          "pointer-events-none rounded-bun-sm bg-bun-ink text-bun-on-ink shadow-bun-menu",
        variant === "composer" && "rounded-bun-xl shadow-bun-menu",
        !position && "invisible",
        className
      )}
      style={{
        width: width === 'anchor' ? undefined : width,
        minWidth: minWidth ? Math.min(minWidth, window.innerWidth - margin * 2) : undefined,
        zIndex,
        ...style,
        ...(position
          ? { left: position.left, maxHeight: position.maxHeight, top: position.top, ...(width === 'anchor' ? { width: position.width } : {}) }
          : null),
        ...dragPosition,
      }}
      {...props}
      // Focus-managing components must wait until the positioned panel is visible.
      focus={position ? props.focus : undefined}
    >
      <DragContext.Provider value={startDrag}>{children}</DragContext.Provider>
    </Component>,
    portalTarget || document.body
  );
});

FloatingPanel.Header = function FloatingPanelHeader({ children, className, ...props }) {
  const startDrag = useContext(DragContext);
  return <div {...props} onMouseDown={startDrag} className={cn('flex items-center justify-between gap-3 border-b border-bun-line px-[18px] py-3', startDrag && 'cursor-grab select-none', className)}>{children}</div>;
};

FloatingPanel.Body = function FloatingPanelBody({ children, className, ...props }) {
  return <div {...props} className={cn('px-[18px] py-3', className)}>{children}</div>;
};

export default FloatingPanel;
